import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CreditCard, Unlink } from 'lucide-react';
import {
  Button,
  EmptyState,
  ErrorState,
  InfoHint,
  Modal,
  Select,
  Skeleton,
  StatusPill,
  toast,
} from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatDate, formatMoney } from '../../lib/format';
import { describeError } from './api';
import {
  useLinkSubscription,
  useRecordStripeCharges,
  useStripeCharges,
  useStripeSubscriptions,
  type LinkTemplateRow,
  type RecurringTemplate,
  type StripeChargeRow,
  type StripeLastCharge,
  type StripeSubscription,
} from './recurring-api';

/**
 * LINK STRIPE SUBSCRIPTIONS — the one place a schedule is tied to the
 * subscription that pays it (Jesse, 2026-09-30: *"we're going to need to link
 * the current subscriptions to those invoices"*).
 *
 * Every subscription on the connected Stripe account is listed, unlinked ones
 * first, each with the schedule it most plausibly pays pre-selected when the
 * server is sure enough to suggest one (two independent signals, no tie). A
 * suggestion is a default in a dropdown, never a link: nothing is linked until
 * somebody presses Link on that row.
 */
export function StripeLinkModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const query = useStripeSubscriptions(open);
  const data = query.data;
  const unlinked = data?.subscriptions.filter((s) => !s.linkedTemplate).length ?? 0;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="Link Stripe subscriptions"
      description="A linked schedule stops drafting on its date. Each Stripe charge issues the invoice, marks it paid and emails the client a receipt."
    >
      <div className="space-y-3">
        {query.isLoading ? (
          <>
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </>
        ) : query.isError ? (
          <ErrorState
            title={describeError(query.error, 'Stripe subscriptions').title}
            message={describeError(query.error, 'Stripe subscriptions').description}
            onRetry={() => query.refetch()}
          />
        ) : !data?.connected ? (
          <EmptyState
            icon={<CreditCard aria-hidden />}
            title="Stripe is not connected"
            description={data?.reason ?? 'Connect Stripe to see its subscriptions here.'}
            action={
              <Link
                to="/settings/integrations/stripe"
                className="font-semibold text-accent-700 hover:underline"
              >
                Connect Stripe →
              </Link>
            }
          />
        ) : (
          <>
            {data.reason && (
              <p className="rounded-lg border border-status-late-border bg-status-late-bg px-3 py-2 text-caption text-status-late">
                {data.reason}
              </p>
            )}

            <div className="flex items-center gap-1.5 text-caption text-grey-600">
              <span>
                {data.subscriptions.length} subscription
                {data.subscriptions.length === 1 ? '' : 's'} on Stripe · {unlinked} not linked
              </span>
              <InfoHint label="How a linked schedule is paid">
                <p>
                  When Stripe charges a linked subscription, the charge pays this schedule's
                  invoice for that period — an existing draft when one matches the amount exactly,
                  otherwise a new invoice issued for what Stripe collected. The payment is recorded
                  and the client's billing contacts get the same receipt the hosted invoice sends.
                </p>
                <p className="mt-2">
                  A charge already recorded by hand is left alone, so nothing is counted twice.
                </p>
                <p className="mt-2">
                  The Stripe webhook must send <code className="font-mono">invoice.paid</code> —
                  add it to the endpoint under Stripe → Developers → Webhooks.
                </p>
              </InfoHint>
            </div>

            {data.missing.map((m) => (
              <MissingLink key={m.subscriptionId} subscriptionId={m.subscriptionId} template={m.template} />
            ))}

            {data.subscriptions.length === 0 && data.missing.length === 0 ? (
              <EmptyState
                title="No active subscriptions"
                description="Stripe has no subscription that can still charge. Create them in Stripe, then link them here."
              />
            ) : (
              <ul className="space-y-2">
                {data.subscriptions.map((sub) => (
                  <SubscriptionRow key={sub.id} sub={sub} templates={data.templates} />
                ))}
              </ul>
            )}
          </>
        )}

        <div className="flex justify-end pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function intervalLabel(sub: StripeSubscription): string {
  const n = sub.intervalCount ?? 1;
  if (!sub.interval) return '';
  return n === 1 ? `/ ${sub.interval}` : `every ${n} ${sub.interval}s`;
}

function templateLabel(t: LinkTemplateRow): string {
  return `${t.clientName} — ${t.subjectPreview} · ${formatMoney(t.amount)} ${t.cadenceLabel.toLowerCase()}${
    t.isActive ? '' : ' (paused)'
  }`;
}

function SubscriptionRow({
  sub,
  templates,
}: {
  sub: StripeSubscription;
  templates: LinkTemplateRow[];
}) {
  const link = useLinkSubscription();
  const [choice, setChoice] = useState(sub.suggestion?.templateId ?? '');
  useEffect(() => setChoice(sub.suggestion?.templateId ?? ''), [sub.suggestion?.templateId]);

  const options = useMemo(
    () => [
      { value: '', label: 'Choose the schedule it pays…' },
      ...templates
        .filter((t) => !t.stripeSubscriptionId)
        .map((t) => ({ value: t.id, label: templateLabel(t) })),
    ],
    [templates],
  );

  const who = sub.customerName ?? sub.customerEmail ?? sub.id;
  const live = sub.status === 'active' || sub.status === 'trialing';

  const submit = (subscriptionId: string | null, templateId: string) =>
    link.mutate(
      { templateId, subscriptionId },
      {
        onSuccess: (result) => {
          toast.success(
            subscriptionId
              ? 'Linked — open the schedule to record charges Stripe already took'
              : 'Unlinked — the schedule drafts on its date again',
          );
          for (const warning of result.warnings) toast.info(warning);
        },
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : 'Could not change the link'),
      },
    );

  return (
    <li
      className={cn(
        'rounded-lg border px-3 py-2.5',
        sub.linkedTemplate ? 'border-grey-200 bg-grey-50/60' : 'border-grey-200',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          <p className="truncate font-semibold text-secondary">{who}</p>
          <p className="truncate text-caption text-grey-600">
            {sub.products.length > 0 ? `${sub.products.join(', ')} · ` : ''}
            {sub.customerEmail && sub.customerName ? `${sub.customerEmail} · ` : ''}
            <span className="font-mono text-grey-400">{sub.id}</span>
          </p>
        </div>
        <div className="text-right">
          <p className="num font-semibold text-secondary">
            {formatMoney(sub.amount)}{' '}
            <span className="font-normal text-grey-500">{intervalLabel(sub)}</span>
          </p>
          <p className="text-caption text-grey-500">
            {sub.nextChargeDate ? `next charge ${formatDate(sub.nextChargeDate)}` : ''}
          </p>
        </div>
      </div>

      {(!live || sub.cancelAtPeriodEnd) && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {!live && <StatusPill status="warning" label={sub.status.replace(/_/g, ' ')} />}
          {sub.cancelAtPeriodEnd && <StatusPill status="neutral" label="Cancels at period end" />}
        </div>
      )}

      {sub.linkedTemplate ? (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <p className="min-w-0 text-caption text-grey-700">
            Pays{' '}
            <span className="font-semibold text-secondary">
              {sub.linkedTemplate.clientName} — {sub.linkedTemplate.subjectPreview}
            </span>
          </p>
          <Button
            variant="ghost"
            size="sm"
            loading={link.isPending}
            onClick={() => submit(null, sub.linkedTemplate!.id)}
          >
            <Unlink className="h-4 w-4" aria-hidden />
            Unlink
          </Button>
        </div>
      ) : (
        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1">
            <Select
              aria-label={`Schedule paid by ${who}`}
              selectSize="sm"
              options={options}
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
            />
            {sub.suggestion && choice === sub.suggestion.templateId && (
              <p className="mt-1 text-caption text-grey-500">
                Suggested — {sub.suggestion.reasons.join(' · ')}
              </p>
            )}
          </div>
          <Button
            size="sm"
            disabled={!choice}
            loading={link.isPending}
            onClick={() => submit(sub.id, choice)}
          >
            Link
          </Button>
        </div>
      )}
    </li>
  );
}

/** A link whose subscription no longer charges. Named, never silently dropped. */
function MissingLink({
  subscriptionId,
  template,
}: {
  subscriptionId: string;
  template: LinkTemplateRow;
}) {
  const link = useLinkSubscription();
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-status-partial-border bg-status-partial-bg px-3 py-2">
      <p className="flex min-w-0 items-start gap-1.5 text-caption text-status-partial">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          <span className="font-semibold">{template.clientName}</span> is linked to{' '}
          <span className="font-mono">{subscriptionId}</span>, which no longer charges on this
          Stripe account — the schedule is not being billed.
        </span>
      </p>
      <Button
        variant="ghost"
        size="sm"
        loading={link.isPending}
        onClick={() =>
          link.mutate(
            { templateId: template.id, subscriptionId: null },
            {
              onSuccess: () => toast.success('Unlinked — the schedule drafts on its date again'),
              onError: (error) =>
                toast.error(error instanceof Error ? error.message : 'Could not unlink'),
            },
          )
        }
      >
        <Unlink className="h-4 w-4" aria-hidden />
        Unlink
      </Button>
    </div>
  );
}

/** One line saying what the last charge did — the schedule's memory of Stripe. */
export function LastChargeLine({
  charge,
  className,
}: {
  charge: StripeLastCharge | null;
  className?: string;
}) {
  if (!charge) {
    return (
      <p className={cn('text-caption text-grey-500', className)}>
        Billed by Stripe · no charge received yet
      </p>
    );
  }
  const trouble = charge.outcome === 'failed' || charge.outcome === 'in-harvest';
  const invoice =
    charge.invoiceId && charge.invoiceNumber ? (
      <Link
        to={`/invoices/${charge.invoiceId}`}
        onClick={(e) => e.stopPropagation()}
        className="whitespace-nowrap font-semibold text-accent-700 hover:underline"
      >
        #{charge.invoiceNumber}
      </Link>
    ) : null;
  const when = formatDate(charge.at);
  return (
    <p
      className={cn(
        'text-caption',
        trouble ? 'font-semibold text-status-late' : 'text-grey-500',
        className,
      )}
      title={charge.message}
    >
      {charge.outcome === 'recorded' || charge.outcome === 'already-recorded' ? (
        <>
          Paid by Stripe {when}
          {invoice && <> · {invoice}</>}
        </>
      ) : charge.outcome === 'recorded-by-hand' ? (
        <>
          Stripe charge {when} was already recorded by hand{invoice && <> on {invoice}</>}
        </>
      ) : charge.outcome === 'in-harvest' ? (
        <>
          Stripe charge {when} — mark {invoice ?? 'it'} paid in Harvest
        </>
      ) : (
        <>
          Stripe charge {when} not recorded — {charge.message}
        </>
      )}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Inside one schedule's editor
// ---------------------------------------------------------------------------

const RECORDABLE = new Set(['pay', 'create']);

/**
 * The schedule's Stripe state, and the catch-up for charges Stripe took before
 * the link existed. Linking happens in `StripeLinkModal` — one control for the
 * idea — so an unlinked schedule only says where that is.
 */
export function ScheduleStripePanel({ template }: { template: RecurringTemplate }) {
  const link = useLinkSubscription();
  const [showCharges, setShowCharges] = useState(false);

  if (!template.stripe) {
    return (
      <p className="flex items-center gap-1.5 text-caption text-grey-500">
        <CreditCard className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          Paid by a Stripe subscription? Link it with{' '}
          <strong className="font-semibold">Link Stripe subscriptions</strong> on the list.
        </span>
      </p>
    );
  }

  return (
    <div className="rounded-lg border border-grey-200">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-body-sm font-semibold text-secondary">
            <CreditCard className="h-4 w-4 shrink-0 text-grey-500" aria-hidden />
            Paid by Stripe subscription
          </p>
          <p className="truncate font-mono text-caption text-grey-400">
            {template.stripe.subscriptionId}
          </p>
          <LastChargeLine charge={template.stripe.lastCharge} />
        </div>
        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-expanded={showCharges}
            onClick={() => setShowCharges((v) => !v)}
          >
            {showCharges ? 'Hide charges' : 'Past charges'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            loading={link.isPending}
            onClick={() =>
              link.mutate(
                { templateId: template.id, subscriptionId: null },
                {
                  onSuccess: () => toast.success('Unlinked — the schedule drafts on its date again'),
                  onError: (error) =>
                    toast.error(error instanceof Error ? error.message : 'Could not unlink'),
                },
              )
            }
          >
            <Unlink className="h-4 w-4" aria-hidden />
            Unlink
          </Button>
        </div>
      </div>
      {showCharges && <PastCharges templateId={template.id} />}
    </div>
  );
}

function PastCharges({ templateId }: { templateId: string }) {
  const query = useStripeCharges(templateId, true);
  const record = useRecordStripeCharges();
  const charges = query.data?.charges ?? [];
  const [picked, setPicked] = useState<Set<string>>(new Set());

  // Everything recordable starts ticked: the plan beside each row already says
  // what it will do, and unticking is how a human disagrees with one.
  useEffect(() => {
    setPicked(
      new Set(charges.filter((c) => RECORDABLE.has(c.action)).map((c) => c.stripeInvoiceId)),
    );
  }, [query.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (query.isLoading) {
    return (
      <div className="border-t border-grey-200 p-3">
        <Skeleton className="h-20" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <p className="border-t border-grey-200 px-3 py-2 text-caption text-status-late">
        {query.error instanceof Error ? query.error.message : 'Could not read the charges from Stripe'}
      </p>
    );
  }
  if (charges.length === 0) {
    return (
      <p className="border-t border-grey-200 px-3 py-2 text-caption text-grey-500">
        No paid charges in the last {query.data?.lookbackDays ?? 120} days.
      </p>
    );
  }

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = () =>
    record.mutate(
      { templateId, ids: [...picked] },
      {
        onSuccess: (result) => {
          toast.success(
            `Recorded ${result.recorded} charge${result.recorded === 1 ? '' : 's'} — no receipts were sent`,
          );
          const failed = result.results.filter((r) => r.outcome === 'failed');
          if (failed.length > 0) toast.error(failed[0].message);
          query.refetch();
        },
        onError: (error) =>
          toast.error(error instanceof Error ? error.message : 'Could not record the charges'),
      },
    );

  return (
    <div className="border-t border-grey-200 px-3 py-2">
      <p className="mb-2 text-caption text-grey-600">
        Charges Stripe collected in the last {query.data?.lookbackDays} days. Recording one sends
        no receipt — the client paid a while ago.
      </p>
      <ul className="space-y-1.5">
        {charges.map((c) => (
          <ChargeRow
            key={c.stripeInvoiceId}
            charge={c}
            checked={picked.has(c.stripeInvoiceId)}
            onToggle={() => toggle(c.stripeInvoiceId)}
          />
        ))}
      </ul>
      <div className="mt-2 flex justify-end">
        <Button
          type="button"
          size="sm"
          disabled={picked.size === 0}
          loading={record.isPending}
          onClick={submit}
        >
          Record {picked.size} charge{picked.size === 1 ? '' : 's'}
        </Button>
      </div>
    </div>
  );
}

function ChargeRow({
  charge,
  checked,
  onToggle,
}: {
  charge: StripeChargeRow;
  checked: boolean;
  onToggle: () => void;
}) {
  const recordable = RECORDABLE.has(charge.action);
  const id = `charge-${charge.stripeInvoiceId}`;
  return (
    <li className="flex items-start gap-2">
      <input
        id={id}
        type="checkbox"
        className="mt-1 h-4 w-4 shrink-0 accent-accent"
        checked={recordable && checked}
        disabled={!recordable}
        onChange={onToggle}
      />
      <label htmlFor={id} className="min-w-0 flex-1 text-caption">
        <span className="flex flex-wrap items-baseline justify-between gap-x-2">
          <span className="font-semibold text-secondary">
            {formatDate(charge.paidOn)}
            {charge.stripeNumber ? (
              <span className="font-normal text-grey-500"> · {charge.stripeNumber}</span>
            ) : null}
          </span>
          <span className="num font-semibold text-secondary">{formatMoney(charge.amount)}</span>
        </span>
        <span className={cn('block', recordable ? 'text-grey-600' : 'text-grey-500')}>
          {charge.sentence}
        </span>
      </label>
    </li>
  );
}
